import { useState, useCallback } from 'react';
import { useApp } from '../components/AppProvider';

interface ErrorHandlerOptions {
  defaultMessage?: string;
  silent?: boolean;
  onError?: (error: any, errorMessage: string) => void;
}

interface UseErrorHandlerResult {
  error: string | null;
  isError: boolean;
  handleError: (error: any, message?: string) => void;
  clearError: () => void;
  withErrorHandling: <T>(fn: () => Promise<T>, options?: ErrorHandlerOptions) => Promise<T | null>;
}

export const useErrorHandler = (defaultOptions: ErrorHandlerOptions = {}): UseErrorHandlerResult => {
  const { toast } = useApp();
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const handleError = useCallback((err: any, customMessage?: string) => {
    console.error('Error caught by handler:', err);
    const isErrorObj = err instanceof Error;
    const errorMessage = customMessage || 
                         (isErrorObj ? err.message : String(err)) || 
                         defaultOptions.defaultMessage || 
                         'An unexpected error occurred';

    setError(errorMessage);
    
    if (!defaultOptions.silent) {
      toast.error(errorMessage);
    }
    
    if (defaultOptions.onError) {
      defaultOptions.onError(err, errorMessage);
    }
  }, [toast, defaultOptions]);
  
  const withErrorHandling = useCallback(async <T>(
    fn: () => Promise<T>,
    options: ErrorHandlerOptions = {}
  ): Promise<T | null> => {
    clearError();
    try {
      return await fn();
    } catch (err) {
      const errorMessage = options.defaultMessage || defaultOptions.defaultMessage;
      handleError(err, errorMessage);
      
      if (options.onError) {
        options.onError(err, errorMessage || (err instanceof Error ? err.message : String(err)));
      }
      
      return null;
    }
  }, [clearError, handleError, defaultOptions]);

  return {
    error,
    isError: error !== null,
    handleError,
    clearError,
    withErrorHandling
  };
};